import {computed, ref} from 'vue';
import {useActivityStore} from '@features/project-view/stores/activity.store';
import {projectInformationEndpoints, projectTermsEndpoints} from '@registry';
import {defineStore} from 'pinia';
import {useAuth, useLogger, useTranslation} from '#imports';
import {escapeHtml} from '@malt/sanitize';
import {withCleanup} from '@malt/utils';
import {useErrorHandler} from '@malt/utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import {
    type AcceptContractAmendmentRequest,
    type CompleteTimeAndMaterialProjectOperationRequest,
    type ContractTripartiteModeResource,
    type DeletePurchaseOrderRequest,
    type ProjectInformationResource,
    ProjectInformationResourceClientCompanyServiceLevelEnum,
    type RenewProjectRequest,
    type SetPurchaseOrderToProjectRequest,
    type TermsResource,
    type AcceptTripartiteContractTermsRequest,
} from '@api';
import {uploadFiles} from '../utils';

/**
 * Basic project information for all apps
 */
export const useProjectInformationStore = defineStore('project-information', () => {
    const logger = useLogger();
    const {handleUnknownError} = useErrorHandler();

    const {t} = useTranslation();
    const authStore = useAuth();

    // activity store
    const activityStore = useActivityStore();

    // fetched data
    const projectInformation = ref<ProjectInformationResource>(null as unknown as ProjectInformationResource);
    const terms = ref<TermsResource>(null as unknown as TermsResource);

    // loading states
    const loadingProjectInformation = ref(false);
    const acceptingContractAmendment = ref(false);
    const updatingPurchaseOrderNumber = ref(false);
    const renewingProject = ref(false);
    const endingProject = ref(false);
    const dismissingProjectRenewal = ref(false);
    const isAppraisalRedirection = ref(false);

    const isProjectCostPlus = computed(() => projectInformation.value.contract.type === 'COST_PLUS');
    const isProjectFixedPrice = computed(() => projectInformation.value.contract.type === 'FIXED_PRICE');

    const isClientProjectOwner = computed(() => {
        return authStore.user?.selectedIdentity === projectInformation.value.client.identityId;
    });

    const isEnterpriseClient = computed(() => {
        return projectInformation.value.clientCompanyServiceLevel === ProjectInformationResourceClientCompanyServiceLevelEnum.Corporate;
    });

    const isClientCompanyAdmin = computed(() => {
        return authStore.getSelectedIdentity?.admin;
    });

    async function fetchProjectInformation(projectId: string, lotId?: string) {
        try {
            loadingProjectInformation.value = true;
            projectInformation.value = await projectInformationEndpoints.getProjectInformation({projectId, lotId});
        } catch (e) {
            logger.error(e);
            return Promise.reject(e);
        } finally {
            loadingProjectInformation.value = false;
        }
    }

    async function fetchProjectInformationByCorrelationId(correlationId: string, lotId?: string) {
        try {
            loadingProjectInformation.value = true;
            projectInformation.value = await projectInformationEndpoints.getProjectInformationByCorrelationId({
                correlationId,
                lotId,
            });
        } catch (e) {
            logger.error(e);
            return Promise.reject(e);
        } finally {
            loadingProjectInformation.value = false;
        }
    }

    async function fetchTerms(projectId: string) {
        try {
            terms.value = await projectTermsEndpoints.getTerms({projectId});
        } catch (e) {
            logger.error(e);
            return Promise.reject(e);
        }
    }

    function buildAcceptContractTermsRequestFromStore(): AcceptTripartiteContractTermsRequest | undefined {
        const pending = terms.value?.pending;

        if (pending?.type === 'TRIPARTITE') {
            const projectTermsId = (pending as ContractTripartiteModeResource).projectTerms?.id;
            if (!projectTermsId) {
                return undefined;
            }
            return {invoicingMode: 'TRIPARTITE', projectTermsId};
        }

        return undefined;
    }

    async function updateTerms(projectId: string) {
        const acceptContractTermsRequest = buildAcceptContractTermsRequestFromStore();
        if (!acceptContractTermsRequest) {
            pushVJoySnackbar({
                props: {
                    message: t('projectclient.projectview.contract-amendment.notification.submit.request-incomplete.message'),
                    level: 'error',
                },
            });
            return Promise.reject(new Error('Incomplete accept contract terms request'));
        }
        try {
            await projectTermsEndpoints.acceptContractTerms({projectId, acceptContractTermsRequest});
            await fetchTerms(projectId);
            await activityStore.fetchProjectActivity(projectId);
            pushVJoySnackbar({
                props: {
                    header: t('projectclient.projectview.contract-amendment.notification.submit.success.header'),
                    message: t('projectclient.projectview.contract-amendment.notification.submit.success.message'),
                    level: 'success',
                },
            });
        } catch (e) {
            handleUnknownError(e, {showSnackbar: true});
            return Promise.reject(e);
        }
    }

    // only needed in ProjectView; the po fields in project + expense validation are sent with the BillingInformation param
    async function putPurchaseOrder(requestParameters: SetPurchaseOrderToProjectRequest): Promise<'applied' | 'too-late' | 'error'> {
        try {
            updatingPurchaseOrderNumber.value = true;
            const submittedPurchaseOrderNumber = requestParameters.purchaseOrderParam.purchaseOrderNumber.trim();
            const response = await projectInformationEndpoints.setPurchaseOrderToProjectRaw(requestParameters);

            if (response.status === 204) {
                pushVJoySnackbar({
                    props: {
                        header: t('projectclient.projectinformation.ponumber.too.late.header'),
                        message: t('projectclient.projectinformation.ponumber.too.late.message'),
                        level: 'warning',
                    },
                });
                return 'too-late';
            }

            if (projectInformation.value) {
                projectInformation.value.lot.purchaseOrderNumber = submittedPurchaseOrderNumber;
                projectInformation.value.clientReference = submittedPurchaseOrderNumber;
            }
            pushVJoySnackbar({
                props: {
                    header: t('projectclient.projectinformation.ponumber.success.detailed.header'),
                    message: t('projectclient.projectinformation.ponumber.success.detailed.message', {
                        0: escapeHtml(submittedPurchaseOrderNumber),
                    }),
                    level: 'success',
                },
            });
            return 'applied';
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({
                props: {
                    message: t('projectclient.projectinformation.ponumber.error'),
                    level: 'error',
                },
            });
            return 'error';
        } finally {
            updatingPurchaseOrderNumber.value = false;
        }
    }

    async function deletePurchaseOrder(requestParameters: DeletePurchaseOrderRequest): Promise<'applied' | 'error'> {
        try {
            updatingPurchaseOrderNumber.value = true;
            await projectInformationEndpoints.deletePurchaseOrder(requestParameters);
            pushVJoySnackbar({
                props: {
                    message: t('projectclient.projectinformation.ponumber.success'),
                    level: 'success',
                },
            });
            return 'applied';
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({
                props: {
                    message: t('projectclient.projectinformation.ponumber.error'),
                    level: 'error',
                },
            });
            return 'error';
        } finally {
            updatingPurchaseOrderNumber.value = false;
        }
    }

    async function uploadProjectAppendixFile(file: File) {
        try {
            if (file) {
                const [fileId] = await uploadFiles([file]);
                await projectInformationEndpoints.putTermsAppendix({
                    projectId: projectInformation.value.projectId,
                    termsAppendixParam: {fileId, fileName: file.name},
                });
                pushVJoySnackbar({props: {level: 'success', message: t('projectclient.projectview.details.documents.appendix.upload.success')}});
                await fetchProjectInformation(projectInformation.value.projectId);
            }
        } catch (e) {
            pushVJoySnackbar({props: {level: 'error', message: t('projectclient.projectview.details.documents.appendix.upload.error')}});
            return Promise.reject(e);
        }
    }

    async function acceptContractAmendment(requestParameters: AcceptContractAmendmentRequest) {
        try {
            acceptingContractAmendment.value = true;
            await projectInformationEndpoints.acceptContractAmendment(requestParameters);
            pushVJoySnackbar({
                props: {
                    header: t('projectclient.projectinformation.contract.acceptance.success'),
                    message: t('projectclient.projectinformation.contract.acceptance.message', {
                        0: escapeHtml(projectInformation.value.freelancer.fullName),
                    }),
                    level: 'success',
                },
            });
            await fetchProjectInformation(projectInformation.value.projectId);
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({
                props: {
                    message: t('projectclient.projectinformation.contract.acceptance.error'),
                    level: 'error',
                },
            });
        } finally {
            acceptingContractAmendment.value = false;
        }
    }

    async function renewProject(requestParameters: RenewProjectRequest) {
        try {
            renewingProject.value = true;
            await projectInformationEndpoints.renewProject(requestParameters);
            pushVJoySnackbar({
                props: {
                    header: t('projectclient.projectview.main.actions.renew.project.success.header'),
                    message: t('projectclient.projectview.main.actions.renew.project.success.body', [projectInformation.value.freelancer.fullName]),
                    level: 'success',
                },
            });
            await fetchProjectInformation(projectInformation.value.projectId);
            await activityStore.fetchProjectActivity();
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({
                props: {
                    header: t('projectclient.projectview.main.error.header'),
                    message: t('projectclient.projectview.main.error.message'),
                    level: 'error',
                },
            });
        } finally {
            renewingProject.value = false;
        }
    }

    async function dismissProjectRenewal(projectId: string, comment?: string) {
        try {
            dismissingProjectRenewal.value = true;
            await projectInformationEndpoints.acknowledgeProjectClientItem({
                projectId,
                acknowledgeProjectClientItemRequest: {
                    type: 'PROJECT_RENEWAL_DISMISSED',
                    comment,
                },
            });
        } catch (e) {
            handleUnknownError(e, {showSnackbar: true});
            throw e;
        } finally {
            dismissingProjectRenewal.value = false;
            await activityStore.fetchProjectActivity(projectId);
        }
    }

    async function endProject(requestParameters: CompleteTimeAndMaterialProjectOperationRequest) {
        try {
            endingProject.value = true;
            await projectInformationEndpoints.completeTimeAndMaterialProject(requestParameters);
            pushVJoySnackbar({
                props: {
                    header: t('projectclient.projectview.main.actions.end.project.success.header'),
                    message: t('projectclient.projectview.main.actions.end.project.success.message', {
                        0: projectInformation.value.freelancer.fullName,
                    }),
                    level: 'success',
                },
            });
            await fetchProjectInformation(projectInformation.value.projectId);
            await activityStore.fetchProjectActivity();
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({
                props: {
                    header: t('projectclient.projectview.main.error.header'),
                    message: t('projectclient.projectview.main.error.message'),
                    level: 'error',
                },
            });
        } finally {
            endingProject.value = false;
        }
    }

    async function timeTravelProject(date: Date) {
        try {
            await projectInformationEndpoints.updateClockOffsetDateTime({
                projectId: projectInformation.value.projectId,
                updateClockOffsetDateTimeRequest: {newDate: date},
            });
            await fetchProjectInformation(projectInformation.value.projectId);
        } catch (e) {
            return Promise.reject(e);
        }
    }

    async function acknowledgeAppraisalRedirection(projectId: string) {
        try {
            isAppraisalRedirection.value = true;
            await projectInformationEndpoints.acknowledgeProjectClientItem({
                projectId,
                acknowledgeProjectClientItemRequest: {
                    type: 'LET_APPRAISAL_REDIRECT',
                },
            });
        } catch (e) {
            handleUnknownError(e, {showSnackbar: true});
            throw e;
        } finally {
            isAppraisalRedirection.value = false;
        }
    }

    return withCleanup({
        fetchProjectInformation,
        fetchProjectInformationByCorrelationId,
        projectInformation,
        fetchTerms,
        updateTerms,
        terms,
        isClientCompanyAdmin,
        loadingProjectInformation,
        putPurchaseOrder,
        deletePurchaseOrder,
        uploadProjectAppendixFile,
        acceptContractAmendment,
        renewProject,
        dismissProjectRenewal,
        timeTravelProject,
        isProjectCostPlus,
        isProjectFixedPrice,
        isClientProjectOwner,
        endProject,
        isEnterpriseClient,
        acceptingContractAmendment,
        renewingProject,
        dismissingProjectRenewal,
        endingProject,
        updatingPurchaseOrderNumber,
        acknowledgeAppraisalRedirection,
        isAppraisalRedirection,
    });
});
